<template>
  <div class="flex flex-col items-center gap-4 p-10">
    <YapIcon name="checkmark-round" class="h-14 w-14 text-green-600" />
    <template v-if="approvalRequired">
      <h2 class="text-3xl">{{ t('webapp.payments.beneficiaryApproval.title') }}</h2>
      <p>
        {{ t('webapp.payments.beneficiaryApproval.txt') }}
      </p>
    </template>
    <template v-else-if="executed">
      <h2 class="text-3xl">{{ t('app.payments.payment_executed.dialog.title') }}</h2>
      <p v-if="!isY2YPayment">
        {{ t('app.payments.payment_executed.dialog.txt') }}
      </p>
    </template>
    <template v-else>
      <h2 class="text-3xl">{{ t('app.payments.payment_saved.dialog.title') }}</h2>
      <p>
        {{ t('app.payments.payment_saved.dialog.txt', [formatIsoToLocaleString(paymentForm.paymentTime)]) }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { isSameDay } from 'date-fns';
import { computed, watch } from 'vue';

import router from '@/router/router';
import { useI18n } from '@/plugins/i18n';
import { RouteNames } from '@/router/routes';
import { SseListener } from '@/utils/general';
import YapIcon from '@/components/YapIcon.vue';
import { isYapealIban } from '@/utils/formats';
import { formatIsoToLocaleString } from '@/utils/locale';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { PaymentFormData } from '@/components/payments/types/payment';

const props = defineProps<{
  paymentForm: PaymentFormData;
  approvalRequired?: boolean;
  beneficiaryUidToBeApproved?: string;
}>();

const { t } = useI18n();
const eventStreamStore = useEventStreamStore();

const executed = computed(() => isSameDay(new Date(), new Date(props.paymentForm.paymentTime)));
const isY2YPayment = isYapealIban(props.paymentForm.beneficiary.iban);

watch(
  () => eventStreamStore.stale_beneficiaries,
  (newVal, oldVal) => {
    if (!newVal || !props.beneficiaryUidToBeApproved) return;

    const beneficiaryUids = Object.keys(newVal);
    if (!beneficiaryUids.includes(props.beneficiaryUidToBeApproved)) return;

    if (newVal[props.beneficiaryUidToBeApproved].extra.signed) {
      eventStreamStore.unstaleUid(props.beneficiaryUidToBeApproved, SseListener.Beneficiary);
      // The beneficiary has been signed -> close modal & redirect to dashboard
      router.push({ name: RouteNames.Dashboard });
    }
  },
  { deep: true }
);
</script>
